/**
 * Салбар, агуулах, байршил — ҮҮСГЭХ · ЗАСАХ.
 *
 * ⚠️ «Салбар» хуудас (`/branches`) нь одоо зөвхөн ҮЙЛ АЖИЛЛАГАА
 *    харуулна: нөөц, ажилтан, шилжүүлэлт. Мөр үүсгэх, засах нь ЭНД.
 *
 * ⚠️ `stores.type` нь салбар (RETAIL) ба агуулах (WAREHOUSE) хоёрыг
 *    ялгана — хоёр өөр хүснэгт БИШ, нэг хүснэгтийн хоёр төрөл.
 *
 * ⚠️ Байршлын `isSellable` / `isCountable` хоёр туг бүх логикийг
 *    хариуцна (CLAUDE.md №9). `ItemStatus`-ыг байршилд ашиглахгүй.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQty } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { LOCATION_TYPE_LABEL, type Location, type Store } from '../../lib/types';
import { Button, Empty, ErrorNote, Field, Input, Modal, Select, Tabs } from '../../components/ui';
import { StoreForm } from '../../components/store-form';
import { MapView, type MapPoint } from '../../components/map-view';
import { DataTable, type Column } from '../../components/data-table';

/** Гурван таб: салбар · агуулах · тэдгээрийн доторх физик байршил. */
type Tab = Store['type'] | 'locations';

const LOCATION_TYPES = [
  'SUPPLIER', 'TRANSIT_INTL', 'WAREHOUSE_INTAKE', 'WAREHOUSE_MAIN', 'TRANSIT_INTERNAL',
  'SALES_FLOOR', 'BACK_ROOM', 'DISPLAY', 'REPAIR', 'QUARANTINE', 'CUSTOMER', 'DISPOSED',
];

export function SetupStoresPage() {
  const { can, readOnly } = useAuth();
  // Салбар нь ADMIN, байршил нь агуулахад ч нээлттэй — `ref.ts`-ийн
  // `setupStores.edit` / `setupStores.location`-той ижил.
  const canStores = can('ADMIN') && !readOnly;
  const canLocations = can('ADMIN', 'WAREHOUSE') && !readOnly;

  const [tab, setTab] = useState<Tab>('RETAIL');

  const q = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
  });

  const stores = q.data?.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'RETAIL', label: 'Салбар', count: stores.filter((s) => s.type === 'RETAIL').length },
          { key: 'WAREHOUSE', label: 'Агуулах', count: stores.filter((s) => s.type === 'WAREHOUSE').length },
          { key: 'locations', label: 'Физик байршил' },
        ]}
      />

      {tab === 'locations' ? (
        <LocationsSection canWrite={canLocations} />
      ) : (
        <StoresSection
          type={tab}
          stores={stores.filter((s) => s.type === tab)}
          loading={q.isPending}
          error={q.error}
          canWrite={canStores}
        />
      )}
    </div>
  );
}

/**
 * Салбар эсвэл агуулахын жагсаалт — НЭГ компонент, төрлөөр ялгагдана.
 *
 * ⚠️ Хоёрыг тусад нь бичээгүй: ялгаа нь зөвхөн `type`, гарчиг, кодын
 *    жишээ гурав. Хуулбарлавал багана нэмэх бүрд хоёр газар засна.
 */
function StoresSection({ type, stores, loading, error, canWrite }: {
  type: Store['type'];
  stores: Store[];
  loading: boolean;
  error: unknown;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState<Store | 'new' | null>(null);
  const isWarehouse = type === 'WAREHOUSE';

  const pins: MapPoint[] = stores
    .filter((s): s is Store & { lat: number; lng: number } => s.lat !== null && s.lng !== null)
    .map((s) => ({
      id: s.id,
      // Тэмдэг дээр КОД бичигдэнэ — 3-4 салбар ойрхон байхад ялгагдана
      code: s.code,
      label: `${s.code} · ${s.name}`,
      lat: s.lat,
      lng: s.lng,
    }));

  const columns: Column<Store>[] = [
    { key: 'code', header: 'Код', cell: (s) => s.code, nowrap: true, width: 'w-[90px]', mobile: 'title' },
    { key: 'name', header: 'Нэр', cell: (s) => s.name },
    {
      key: 'manager',
      header: 'Хариуцагч',
      mobile: 'value',
      cell: (s) =>
        s.manager
          ? <span className="text-pretty">{s.manager.fullName}</span>
          : <span className="text-ink-3">Хариуцагчгүй</span>,
    },
    { key: 'address', header: 'Хаяг', cell: (s) => s.address ?? '—' },
    { key: 'phone', header: 'Утас', cell: (s) => s.phone ?? '—', nowrap: true },
    {
      key: 'map',
      header: 'Байршил',
      align: 'right',
      cell: (s) =>
        s.lat !== null && s.lng !== null
          ? <span className="text-accent-700">Тэмдэглэсэн</span>
          : <span className="text-ink-3">—</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold">
            {isWarehouse ? 'Агуулах' : 'Салбар'}
          </h2>
          <span className="num ml-auto text-[12.5px] text-ink-2">
            {formatQty(stores.length)} нэгж · {formatQty(pins.length)} байршилтай
          </span>
          {canWrite ? (
            <Button onClick={() => setEditing('new')}>
              ＋ {isWarehouse ? 'Агуулах' : 'Салбар'} нэмэх
            </Button>
          ) : (
            <span className="text-[11.5px] text-ink-3">Нэмэх эрх зөвхөн админд</span>
          )}
        </div>

        {error ? <ErrorNote>{(error as Error).message}</ErrorNote> : null}
        <DataTable
          rows={stores}
          columns={columns}
          rowKey={(s) => s.id}
          onRowClick={canWrite ? (s) => setEditing(s) : undefined}
          loading={loading}
          empty={<Empty>{isWarehouse ? 'Агуулах' : 'Салбар'} бүртгэгдээгүй</Empty>}
        />
      </section>

      {/* Газрын зураг — байршил тэмдэглэсэн нэгж байвал л утгатай */}
      {pins.length > 0 ? (
        <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
          <div className="flex flex-wrap items-baseline gap-3">
            <h2 className="text-base font-semibold">Газрын зураг</h2>
            <span className="text-[12.5px] text-ink-2">
              {formatQty(stores.length - pins.length)} нэгж байршил тэмдэглээгүй
            </span>
          </div>
          <MapView points={pins} height="h-72" />
        </section>
      ) : null}

      {editing ? (
        <StoreForm
          store={editing === 'new' ? null : editing}
          fixedType={type}
          onClose={() => setEditing(null)}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------ Байршил
function LocationsSection({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Location | 'new' | null>(null);

  const q = useQuery({
    queryKey: ['ref-locations-all'],
    queryFn: () => api<Envelope<Location[]>>('/ref/locations'),
  });

  const columns: Column<Location>[] = [
    { key: 'code', header: 'Код', cell: (l) => l.code, nowrap: true, width: 'w-[140px]', mobile: 'title' },
    { key: 'name', header: 'Нэр', cell: (l) => l.name },
    { key: 'store', header: 'Салбар', cell: (l) => l.store?.name ?? '—' },
    { key: 'type', header: 'Төрөл', cell: (l) => <span className="text-ink-2">{LOCATION_TYPE_LABEL[l.type] ?? l.type}</span> },
    { key: 'sellable', header: 'Зарагдана', cell: (l) => (l.isSellable ? '✓' : '—'), mobile: 'value' },
    { key: 'countable', header: 'Тоологдоно', cell: (l) => (l.isCountable ? '✓' : '—') },
    { key: 'state', header: 'Төлөв', cell: (l) => (l.isActive === false ? 'Идэвхгүй' : 'Идэвхтэй') },
  ];

  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Физик байршил</h2>
        <span className="text-[12.5px] text-ink-2">
          «Зарагдана», «Тоологдоно» хоёр тохиргоо бүх логикийг хариуцна
        </span>
        {canWrite ? (
          <Button className="ml-auto" onClick={() => setEditing('new')}>＋ Байршил нэмэх</Button>
        ) : null}
      </div>

      {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
      <DataTable
        rows={q.data?.data ?? []}
        columns={columns}
        rowKey={(l) => l.id}
        onRowClick={canWrite ? (l) => setEditing(l) : undefined}
        rowClass={(l) => (l.isActive === false ? 'opacity-55' : '')}
        loading={q.isPending}
        empty={<Empty>Байршил алга</Empty>}
      />

      {editing ? (
        <LocationForm
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await qc.invalidateQueries({ queryKey: ['ref-locations-all'] });
            await qc.invalidateQueries({ queryKey: ['ref-locations'] });
          }}
        />
      ) : null}
    </section>
  );
}

function LocationForm({ location, onClose, onDone }: {
  location: Location | null; onClose: () => void; onDone: () => void;
}) {
  const isNew = !location;
  const [code, setCode] = useState(location?.code ?? '');
  const [name, setName] = useState(location?.name ?? '');
  const [type, setType] = useState(location?.type ?? 'WAREHOUSE_MAIN');
  const [storeId, setStore] = useState(location?.storeId ?? '');
  const [isSellable, setSellable] = useState(location?.isSellable ?? false);
  const [isCountable, setCountable] = useState(location?.isCountable ?? true);
  const [isActive, setActive] = useState(location?.isActive !== false);

  const stores = useQuery({ queryKey: ['ref-stores'], queryFn: () => api<Envelope<Store[]>>('/ref/stores') });

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api('/ref/locations', {
            method: 'POST',
            body: {
              code: code.toUpperCase(), name, type,
              ...(storeId ? { storeId } : {}),
              isSellable, isCountable,
            },
          })
        : api(`/ref/locations/${location.id}`, {
            method: 'PATCH',
            body: { name, isSellable, isCountable, isActive },
          }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={isNew ? 'Шинэ байршил' : `Байршил ${location.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!name || (isNew && !code) || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      {isNew ? (
        <>
          <Field label="Код">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="S4-FLOOR" className="num" />
          </Field>
          <Field label="Төрөл">
            <Select value={type} onChange={setType} options={LOCATION_TYPES.map((t) => ({ value: t, label: LOCATION_TYPE_LABEL[t] ?? t }))} />
          </Field>
          <Field label="Салбар">
            <Select value={storeId} onChange={setStore} placeholder="Хамаарахгүй"
              options={(stores.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />
          </Field>
        </>
      ) : null}
      <Field label="Нэр"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input type="checkbox" checked={isSellable} onChange={(e) => setSellable(e.target.checked)} className="size-4 accent-accent" />
          Зарагдах байршил
        </label>
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input type="checkbox" checked={isCountable} onChange={(e) => setCountable(e.target.checked)} className="size-4 accent-accent" />
          Тооллогод орно
        </label>
        {!isNew ? (
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            <input type="checkbox" checked={isActive} onChange={(e) => setActive(e.target.checked)} className="size-4 accent-accent" />
            Идэвхтэй
          </label>
        ) : null}
      </div>
      <p className="text-[11.5px] text-ink-3">
        Зарагдах байршилд шошгогүй гутал орохгүй — серийн дугаар заавал.
      </p>
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}
